import React, {useState, useEffect, useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {labels} from '../../../../util/utils';
import Modal from '../../Modal/Modal';
import Loader from '../../LoadingIndicator/LoadingIndicator';
import {
	getAccountById,
	cleanAccount,
	updateAccount,
	createAccount,
	getAccounts
} from '../../../../actions/Account/accountactions';
import {getAvailablePSPIndexes} from '../../../../actions/PSPIndexes/pspIndexesActions';
import EditAccountHeaderModal from './EditAccountHeaderModal';
import EditAccountAssertionsModal from './EditAccountAssertionsModal';
import EditAccountPSPIndexModal from './EditAccountPSPIndexModal';
import EditAccountSwitchWithoutSaveModal from './EditAccountSwitchWithoutSaveModal';
import {pspIndexesConstants, assertionsFormat} from '../../../../util/uiconstants';
import {RiskTypesValidExpands, getRiskTypes} from '../../../../actions/RiskType/risktypeactions';
import {getAccountsAllIds, getSliceById} from '../../../../selectors/Ares/selectors';
import {AccountType, clientSideEventNames} from '../../../../util/uiconstants';
import Fetch from '../../Fetch/fetch';
import Toast from '../../BaseComponentWrappers/MotifToast';
import _ from 'lodash';
import {useEngagementId} from '../../../../util/customHooks';

const EditAccountModalV2 = ({show, onClose, callFrom, isAresView}) => {
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const engagementId = useEngagementId();
	const existingAccounts = useSelector(getAccountsAllIds);
	const accountsById = useSelector((state) => getSliceById(state, 'accounts'));
	const [changesWithoutSave, setChangesWithoutSave] = useState(false);
	const [showSureModal, setShowSureModal] = useState(false);
	const [toasterVisible, setToasterVisible] = useState(false);
	const [tosterLabel, setToasterLabel] = useState();
	const [showErrors, setShowErrors] = useState(callFrom ? true : false);
	const [selectedPSPIndexes, setSelectedPSPIndexes] = useState([]);
	const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
	const [createNewAccount, setCreateNewAccount] = useState(false);
	const [nextAccount, setNextAccount] = useState();
	const [calledFrom, setCalledFrom] = useState(callFrom);
	const selectedAccount = useSelector((state) => state.account);
	const [editAccount, setEditAccount] = useState(selectedAccount);

	const handlerOnSelectAccountChange = (account) => {
		setEditAccount(selectedAccount);
		selectAccountChange(account);
		setCalledFrom(undefined);
	};

	const closeSureModal = () => {
		setShowSureModal(false);
	};

	const selectAccountChange = (account) => {
		setChangesWithoutSave(false);
		setIsLoading(true);
		dispatch(getAccountById(Number(engagementId), Number(account.value))).finally(() => {
			setIsLoading(false);
		});
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setToasterVisible(false);
		}, 3000);

		return () => clearTimeout(timer);
	});

	useEffect(() => {
		if (selectedAccount !== editAccount) {
			setEditAccount(selectedAccount);
		}
	}, [selectedAccount]);

	const accountList = () => {
		if (!_.isEmpty(existingAccounts)) {
			let arrayAccounts = existingAccounts.map((id) => {
				return {
					value: id,
					label: accountsById[id].accountName
				};
			});
			return arrayAccounts;
		}
	};

	const handlerOnChange = (prop) => {
		let account = {...selectedAccount, ...editAccount, hasEstimate: selectedAccount.hasEstimate, ...prop};
		setEditAccount(account);
		setChangesWithoutSave(true);
	};

	const handleClose = async () => {
		setIsLoading(true);
		await getRiskTypesWithAssertionsAndAccounts();

		setChangesWithoutSave(false);
		dispatch(cleanAccount()).finally(() => {
			onClose();
			setIsLoading(false);
			window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
		});
	};

	const pspIndexSelectionHandler = (items) => {
		const newPSPIndexes = items.map((item) => {
			return {
				id: item.id,
				cgpspIndexID: item.pspIndexId,
				pspIndexTitle: item.label,
				isEstimate: item.isEstimate,
				knowledgeContentID: item.knowledgeContentID,
				knowledgeModifyDate: item.knowledgeModifyDate
			};
		});
		setSelectedPSPIndexes(() => [...newPSPIndexes]);
		setEditAccount({...selectedAccount, ...editAccount});
	};

	const onSaveAndCloseChangesHandler = async () => {
		if (_.isEmpty(editAccount)) return;
		if (!editAccountHasErrors(editAccount)) {
			try {
				if (!isLoading) {
					setShowErrors(false);
					await saveData();
					await getAllAccounts();
					setEditAccount(editAccount);
					setChangesWithoutSave(false);
					setToasterVisible(true);
					await handleClose();
				}
			} catch {
				setShowErrors(true);
			} finally {
				setIsLoading(false);
				setIsSaveButtonDisabled(false);
				setCalledFrom(undefined);
			}
		}
	};

	const onSaveChangesHandler = async () => {
		if (_.isEmpty(editAccount)) {
			setShowErrors(true);
			return;
		}

		if (!editAccountHasErrors(editAccount)) {
			if (!isLoading) {
				try {
					setShowErrors(false);
					await saveData();
					await getAllAccounts();
					setEditAccount(editAccount);
					setChangesWithoutSave(false);
					setToasterVisible(true);
					setEditAccount({});
					setCreateNewAccount(false);
				} finally {
					setIsLoading(false);
					setIsSaveButtonDisabled(false);
					setCalledFrom(undefined);
					setShowErrors(false);
				}
			}
		}
	};

	const editAccountHasErrors = (account) => {
		let result = _.isEmpty(account.accountName?.trim());
		if (!result) result = selectedPSPIndexes.length < pspIndexesConstants.MIN_SELECTED_PSP_INDEXES;
		if (result) setIsSaveButtonDisabled(false);
		setShowErrors(result);
		return result;
	};

	async function getRiskTypesWithAssertionsAndAccounts() {
		const urlParams = {
			isActive: true,
			expand: `${RiskTypesValidExpands.Risks},${RiskTypesValidExpands.Assertions.Assertions},${RiskTypesValidExpands.Assertions.Accounts},${RiskTypesValidExpands.RiskFactorRelations}`
		};
		dispatch(getRiskTypes(engagementId, urlParams));
	}

	const saveData = async () => {
		const updateAccountAsync = async (account) => {
			dispatch(updateAccount(engagementId, editAccount.id, account));
		};
		const createAccountAsync = async (account) => {
			dispatch(createAccount(engagementId, account));
		};

		let assertionsArray = [];
		if (Object.keys(editAccount.assertions ? editAccount.assertions : []).length > 0) {
			editAccount.assertions.map((item) => {
				let selectedItem = assertionsFormat.find((x) => x.id === item.knowledgeAssertionId);
				const assertionSave = {
					assertionID: item.id,
					accountId: editAccount.id,
					assertionName: selectedItem.assertionname,
					assertionAbbreviation: item.assertionAbbreviation,
					knowledgeAssertionId: item.knowledgeAssertionId,
					displayOrder: item.displayOrder,
					engagementId: Number(engagementId)
				};
				assertionsArray.push(assertionSave);
			});
		}

		const saveAccount = {
			accountName: editAccount.accountName,
			accountType: calledFrom ? AccountType.Significant : editAccount.accountTypeId,
			statementType: editAccount.statementTypeId,
			hasEstimate: editAccount.hasEstimate,
			assertionTypes: assertionsArray.map((item) => item.knowledgeAssertionId),
			pspIndexes: selectedPSPIndexes.map((item) => {
				return {
					knowledgeContentID: item.knowledgeContentID.toString(),
					isEstimate: item.isEstimate,
					pspIndexTitle: item.pspIndexTitle
				};
			})
		};

		if (!_.isEmpty(editAccount)) {
			setIsLoading(true);
			setIsSaveButtonDisabled(true);
			if (editAccount.id) {
				await updateAccountAsync(saveAccount);
				setToasterLabel(labels.updateAccountLabel);
			} else {
				await createAccountAsync(saveAccount);
				setToasterLabel(labels.createAccountLabel);
			}
		}
	};

	const getAllAccounts = async () => {
		setIsLoading(true);
		dispatch(
			getAccounts(engagementId, {
				orderBy: 'accountName',
				includeRelatedRisks: true,
				includePspIndexes: true,
				includeAssertions: true,
				includeHelix: true
			})
		);
	};

	const fetch = useCallback(() => dispatch(getAvailablePSPIndexes(Number(engagementId))), [dispatch, engagementId]);

	return (
		<Modal
			show={show}
			title={labels.manageAccountsModalTitle}
			closeTitle={labels.modalCancelTitle}
			modalsize={'medium'}
			showButtons
			confirmBtnTitle={labels.modalConfirmButton}
			modalContainerClass="editaccounts-modal"
			modalbodystyle="editaccounts-modal-body"
			closeBtnTitle={labels.modalCancelTitle}
			onOkClick={onSaveChangesHandler}
			onSaveAndCreateNew={onSaveAndCloseChangesHandler}
			onHide={handleClose}
			isOkButtonDisabled={isSaveButtonDisabled || !changesWithoutSave}
			isCancelButtonDisabled={isLoading}
			showSaveAndCreateNewButton
			isSaveAndCreateNewButtonDisabled={isLoading || !changesWithoutSave}
			saveAndCreateNewBtnTitle={labels.modalSaveAndClose}
			isAresView={isAresView}
		>
			<StyledEditAccountModalBody className="customScrollbar">
				{toasterVisible && (
					<StyledMotifToast
						variant="success"
						className="bannerLabel"
						onClose={() => setToasterVisible(false)}
						children={tosterLabel.replace('{0}', selectedAccount.accountName)}
					/>
				)}
				<Fetch fetch={fetch}>
					<Loader show={isLoading} styleName={'inFrontView'} />
					<section className="header-message">{labels.editAccountInstructionalText}</section>
					<EditAccountHeaderModal
						accountsList={accountList()}
						editAccount={editAccount}
						handlerOnSelectAccountChange={handlerOnSelectAccountChange}
						handlerOnAccountNameChange={handlerOnChange}
						showErrors={showErrors}
						hideEditAccountModal={handleClose}
						callFrom={calledFrom}
						engagementId={engagementId}
						changesWithoutSave={changesWithoutSave}
						setShowSureModal={setShowSureModal}
						setNextAccount={setNextAccount}
						createNewAccount={createNewAccount}
						setCreateNewAccount={setCreateNewAccount}
						isAresView={isAresView}
						setToasterLabel={setToasterLabel}
						setToasterVisible={setToasterVisible}
					/>
					<EditAccountAssertionsModal
						editAccount={selectedAccount}
						handlerOnSelectDesignationChange={handlerOnChange}
						handlerOnSelectedStatementTypeChange={handlerOnChange}
						handlerOnSelectedAssertionsChange={handlerOnChange}
						handlerOnHasEstimateChange={handlerOnChange}
						showErrors={showErrors}
						callFrom={calledFrom}
						createNewAccount={createNewAccount}
						setCalledFrom={setCalledFrom}
						isAresView={isAresView}
					/>
					<EditAccountPSPIndexModal
						selectedPSPIndexes={editAccount?.pspIndexes}
						onPSPIndexSelectionChange={pspIndexSelectionHandler}
						showErrors={showErrors}
						setChangesWithoutSave={setChangesWithoutSave}
						isDropdownDisabled={_.isEmpty(editAccount) && !createNewAccount}
					/>
					<EditAccountSwitchWithoutSaveModal
						show={showSureModal}
						onClose={closeSureModal}
						setShowSureModal={setShowSureModal}
						nextAccount={nextAccount}
						selectAccountChange={selectAccountChange}
						setCreateNewAccount={setCreateNewAccount}
					/>
				</Fetch>
			</StyledEditAccountModalBody>
		</Modal>
	);
};

EditAccountModalV2.propTypes = {
	show: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	callFrom: PropTypes.string
};

export default EditAccountModalV2;

const StyledEditAccountModalBody = styled.section`
	display: flex;
	flex-direction: column;
	height: 100%;
	.header-message {
		padding: 0 0 var(--px-15) 0;
		margin-bottom: var(--px-20);
		margin-right: var(--px-20);
		color: var(--neutrals-900);
		border-bottom: var(--px-1) solid var(--neutrals-300);
		font-size: var(--px-14);
		line-height: var(--px-22);
		max-height: var(--px-90);
	}
`;

const StyledMotifToast = styled(Toast)`
	&.bannerLabel {
		position: absolute;
		width: calc(100% - var(--px-20));
		left: var(--px-10);
		border-radius: var(--px-12);
		top: var(--px-8);
		z-index: 99;
	}
`;
